/* eslint-disable react/forbid-prop-types */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import RcDrawer from 'rc-drawer';
import ConfigProvider from '../config-provider';
import Button from '../button';
import classNames from 'classnames';
import omit from 'omit.js';
import { dom } from '../util';

const DrawerContext = React.createContext(null);

class Drawer extends Component {
  static propTypes = {
    prefix: PropTypes.string,
    closable: PropTypes.bool,
    destroyOnClose: PropTypes.bool,
    getContainer: PropTypes.any,
    maskClosable: PropTypes.bool,
    mask: PropTypes.bool,
    maskStyle: PropTypes.object,
    style: PropTypes.object,
    drawerStyle: PropTypes.object,
    headerStyle: PropTypes.object,
    bodyStyle: PropTypes.object,
    title: PropTypes.node,
    visible: PropTypes.bool,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    zIndex: PropTypes.number,
    prefixCls: PropTypes.string,
    push: PropTypes.bool,
    placement: PropTypes.oneOf(['top', 'right', 'bottom', 'left']),
    onClose: PropTypes.func,
    afterVisibleChange: PropTypes.func,
    className: PropTypes.string,
    handler: PropTypes.bool,
    footer: PropTypes.node,
    footerStyle: PropTypes.object,
    keyboard: PropTypes.bool,
    level: PropTypes.any,
  };

  static defaultProps = {
    prefix: 'next-',
    width: 256,
    height: 256,
    closable: true,
    placement: 'right',
    maskClosable: true,
    level: null,
    keyboard: true,
    mask: true,
  };

  constructor(props) {
    super(props);
    this.parentDrawer = this || null;
    this.destroyClose = false;
    this.state = {
      push: false,
    };
  }

  componentDidMount() {
    const { visible } = this.props;
    if (visible && this.parentDrawer) {
      this.parentDrawer.push();
    }
  }

  componentDidUpdate(preProps) {
    const { visible } = this.props;
    if (preProps.visible !== visible && this.parentDrawer) {
      if (visible) {
        this.parentDrawer.push();
      } else {
        this.parentDrawer.pull();
      }
    }
  }

  componentWillUnmount() {
    // unmount drawer in child, clear push.
    if (this.parentDrawer) {
      this.parentDrawer.pull();
      this.parentDrawer = null;
    }
  }

  push = () => {
    this.setState({
      push: true,
    });
  };

  pull = () => {
    this.setState({
      push: false,
    });
  };

  onDestroyTransitionEnd = () => {
    const isDestroyOnClose = this.getDestroyOnClose();
    if (!isDestroyOnClose) {
      return;
    }
    if (!this.props.visible) {
      this.destroyClose = true;
      this.forceUpdate();
    }
  };

  getDestroyOnClose = () => this.props.destroyOnClose && !this.props.visible;

  // get drawer push width or height
  getPushTransform = placement => {
    if (placement === 'left' || placement === 'right') {
      return `translateX(${placement === 'left' ? 180 : -180}px)`;
    }
    if (placement === 'top' || placement === 'bottom') {
      return `translateY(${placement === 'top' ? 180 : -180}px)`;
    }
  };

  getRcDrawerStyle = () => {
    const { zIndex, placement, style } = this.props;
    const { push } = this.state;
    return {
      zIndex,
      transform: push ? this.getPushTransform(placement) : undefined,
      ...style,
    };
  };

  renderHeader() {
    const { title, prefix, closable, headerStyle } = this.props;
    if (!title && !closable) {
      return null;
    }

    const headerClassName = title ? `${prefix}drawer-header` : `${prefix}drawer-header-no-title`;
    return (
      <div className={headerClassName} style={headerStyle}>
        {title && <div className={`${prefix}drawer-title`}>{title}</div>}
        {closable && this.renderCloseIcon()}
      </div>
    );
  }

  renderFooter() {
    const { footer, footerStyle, prefix } = this.props;
    if (!footer) {
      return null;
    }

    const footerClassName = `${prefix}drawer-footer`;
    return (
      <div className={footerClassName} style={footerStyle}>
        {footer}
      </div>
    );
  }

  renderCloseIcon() {
    const { closable, prefix, onClose } = this.props;
    return (
      closable && (
        <Button.Icon
          name="remove"
          type="secondary"
          color="gray"
          className={`${prefix}drawer-close`}
          onClick={onClose}
          style={{
            '--scroll-bar': `${dom.scrollbar().width}px`,
          }}
        />
      )
    );
  }

  // render drawer body dom
  renderBody = () => {
    const { bodyStyle, drawerStyle, prefix, visible } = this.props;
    if (this.destroyClose && !visible) {
      return null;
    }
    this.destroyClose = false;

    const containerStyle = {};

    const isDestroyOnClose = this.getDestroyOnClose();

    if (isDestroyOnClose) {
      // Increase the opacity transition, delete children after closing.
      containerStyle.opacity = 0;
      containerStyle.transition = 'opacity .3s';
    }

    return (
      <div
        className={`${prefix}drawer-wrapper-body`}
        style={{ ...containerStyle, ...drawerStyle }}
        onTransitionEnd={this.onDestroyTransitionEnd}
      >
        {this.renderHeader()}
        <div className={`${prefix}drawer-body`} style={bodyStyle}>
          {this.props.children}
        </div>
        {this.renderFooter()}
      </div>
    );
  };

  renderProvider = value => {
    const { prefix, placement, className, width, height, mask, ...rest } = this.props;
    const haveMask = mask ? '' : 'no-mask';
    this.parentDrawer = value;
    const offsetStyle = {};
    if (placement === 'left' || placement === 'right') {
      offsetStyle.width = width;
    } else {
      offsetStyle.height = height;
    }

    const drawerClassName = classNames(className, haveMask);
    return (
      <DrawerContext.Provider value={this}>
        <RcDrawer
          handler={false}
          {...omit(rest, [
            'zIndex',
            'style',
            'closable',
            'destroyOnClose',
            'drawerStyle',
            'headerStyle',
            'bodyStyle',
            'footerStyle',
            'footer',
            'locale',
            'title',
            'push',
            'visible',
            'getPopupContainer',
            'rootPrefixCls',
            'getPrefixCls',
            'renderEmpty',
          ])}
          {...offsetStyle}
          prefixCls={`${prefix}drawer`}
          open={this.props.visible}
          placement={placement}
          showMask={mask}
          style={this.getRcDrawerStyle()}
          className={drawerClassName}
        >
          {this.renderBody()}
        </RcDrawer>
      </DrawerContext.Provider>
    );
  };

  render() {
    return <DrawerContext.Consumer>{this.renderProvider}</DrawerContext.Consumer>;
  }
}

export default ConfigProvider.config(Drawer);
